import React, { useState, useEffect, useContext } from 'react';
import { Container, Row, Col, Card, ProgressBar, Button, Badge, Alert, Spinner } from 'react-bootstrap';
import { FiZap, FiTrendingUp, FiCalendar, FiShoppingCart, FiFileText, FiMessageSquare, FiMic, FiCheckSquare } from 'react-icons/fi';
import axios from '../utils/axios';
import { ThemeContext } from '../utils/ThemeContext';

const UsageDashboard = () => {
  const { theme } = useContext(ThemeContext);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [usageData, setUsageData] = useState(null);
  const [costs, setCosts] = useState(null);
  const [purchasing, setPurchasing] = useState(false);

  useEffect(() => {
    fetchUsageData();
    fetchCosts();
  }, []);

  const fetchUsageData = async () => {
    try {
      const response = await axios.get('/api/credits/usage?days=30');
      if (response.data.success) {
        setUsageData(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching usage:', err);
      setError('Failed to load usage data');
    } finally {
      setLoading(false);
    }
  };

  const fetchCosts = async () => {
    try {
      const response = await axios.get('/api/credits/costs');
      if (response.data.success) {
        setCosts(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching costs:', err);
    }
  };

  const handlePurchaseCredits = async (credits) => {
    setPurchasing(true);
    try {
      const response = await axios.post('/api/credits/purchase', { credits });
      if (response.data.success) {
        if (response.data.url) {
          // Redirect to Stripe checkout
          window.location.href = response.data.url;
        } else {
          // Development mode - credits added directly
          alert(`Added ${credits} credits!`);
          fetchUsageData();
        }
      }
    } catch (err) {
      console.error('Error purchasing credits:', err);
      alert('Failed to initiate purchase');
    } finally {
      setPurchasing(false);
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'transcription': return <FiMic />;
      case 'summary': return <FiFileText />;
      case 'document_upload': return <FiFileText />;
      case 'document_chat': return <FiMessageSquare />;
      case 'meeting_chat': return <FiMessageSquare />;
      case 'action_items': return <FiCheckSquare />;
      default: return <FiZap />;
    }
  };

  const getActionLabel = (action) => {
    const labels = {
      transcription: 'Transcription',
      summary: 'AI Summaries',
      document_upload: 'Document Analysis',
      document_chat: 'Document Chat',
      meeting_chat: 'Meeting Chat',
      action_items: 'Action Items'
    };
    return labels[action] || action;
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading usage data...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-5">
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }

  const balance = usageData?.balance || { monthly: 0, purchased: 0, total: 0 };
  const isUnlimited = balance.isUnlimited;
  const usagePercent = isUnlimited ? 0 : Math.min(100, ((usageData?.planAllocation - balance.monthly) / usageData?.planAllocation) * 100);

  return (
    <Container className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1">Usage Dashboard</h2>
          <p className={`mb-0 ${theme === 'dark' ? 'text-light-emphasis' : 'text-muted'}`}>
            Monitor your credit usage and purchase more when needed
          </p>
        </div>
      </div>

      {/* Credit Balance Cards */}
      <Row className="mb-4">
        <Col md={4}>
          <Card className={`h-100 ${theme === 'dark' ? 'bg-dark text-white border-secondary' : ''}`}>
            <Card.Body>
              <div className="d-flex align-items-center mb-3">
                <div className="rounded-circle p-2 bg-primary bg-opacity-10 me-3">
                  <FiZap className="text-primary" size={24} />
                </div>
                <div>
                  <small className={theme === 'dark' ? 'text-light-emphasis' : 'text-muted'}>Monthly Credits</small>
                  <h3 className="mb-0">
                    {isUnlimited ? '∞' : balance.monthly}
                    {!isUnlimited && <small className={theme === 'dark' ? 'text-light-emphasis' : 'text-muted'}> / {usageData?.planAllocation}</small>}
                  </h3>
                </div>
              </div>
              {!isUnlimited && (
                <ProgressBar 
                  now={100 - usagePercent} 
                  variant={usagePercent > 80 ? 'danger' : usagePercent > 50 ? 'warning' : 'success'}
                  className="mb-2"
                />
              )}
              {isUnlimited ? (
                <Badge bg="success">Unlimited Plan</Badge>
              ) : (
                <small className={theme === 'dark' ? 'text-light-emphasis' : 'text-muted'}>
                  {Math.round(usagePercent)}% used this month
                </small>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col md={4}>
          <Card className={`h-100 ${theme === 'dark' ? 'bg-dark text-white border-secondary' : ''}`}>
            <Card.Body>
              <div className="d-flex align-items-center mb-3">
                <div className="rounded-circle p-2 bg-success bg-opacity-10 me-3">
                  <FiShoppingCart className="text-success" size={24} />
                </div>
                <div>
                  <small className={theme === 'dark' ? 'text-light-emphasis' : 'text-muted'}>Purchased Credits</small>
                  <h3 className="mb-0">{balance.purchased}</h3>
                </div>
              </div>
              <small className={theme === 'dark' ? 'text-light-emphasis' : 'text-muted'}>
                Never expires • Used after monthly
              </small>
            </Card.Body>
          </Card>
        </Col>

        <Col md={4}>
          <Card className={`h-100 ${theme === 'dark' ? 'bg-dark text-white border-secondary' : ''}`}>
            <Card.Body>
              <div className="d-flex align-items-center mb-3">
                <div className="rounded-circle p-2 bg-info bg-opacity-10 me-3">
                  <FiCalendar className="text-info" size={24} />
                </div>
                <div>
                  <small className={theme === 'dark' ? 'text-light-emphasis' : 'text-muted'}>Resets On</small>
                  <h3 className="mb-0">
                    {usageData?.resetDate 
                      ? new Date(usageData.resetDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                      : 'N/A'
                    }
                  </h3>
                </div>
              </div>
              <small className={theme === 'dark' ? 'text-light-emphasis' : 'text-muted'}>
                Monthly credits reset to {usageData?.planAllocation || 0}
              </small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Usage by Feature */}
      <Row className="mb-4">
        <Col md={8}>
          <Card className={theme === 'dark' ? 'bg-dark text-white border-secondary' : ''}>
            <Card.Header className={theme === 'dark' ? 'border-secondary' : ''}>
              <div className="d-flex align-items-center">
                <FiTrendingUp className="me-2" />
                <span>Usage by Feature (Last 30 Days)</span>
              </div>
            </Card.Header>
            <Card.Body>
              {usageData?.usageByType && Object.keys(usageData.usageByType).length > 0 ? (
                <div className="usage-breakdown">
                  {Object.entries(usageData.usageByType).map(([action, data]) => (
                    <div key={action} className="d-flex align-items-center justify-content-between py-2 border-bottom">
                      <div className="d-flex align-items-center">
                        <span className="me-3 text-primary">{getActionIcon(action)}</span>
                        <div>
                          <div className="fw-medium">{getActionLabel(action)}</div>
                          <small className={theme === 'dark' ? 'text-light-emphasis' : 'text-muted'}>
                            {data.count} {data.count === 1 ? 'use' : 'uses'}
                          </small>
                        </div>
                      </div>
                      <Badge bg="primary" className="px-3 py-2">
                        {data.credits} credits
                      </Badge>
                    </div>
                  ))}
                  <div className="d-flex justify-content-between pt-3 fw-bold">
                    <span>Total Used</span>
                    <span>{usageData.totalUsed} credits</span>
                  </div>
                </div>
              ) : (
                <div className="text-center py-4">
                  <FiZap size={48} className={theme === 'dark' ? 'text-light-emphasis' : 'text-muted'} />
                  <p className={`mt-3 ${theme === 'dark' ? 'text-light-emphasis' : 'text-muted'}`}>
                    No usage recorded in the last 30 days
                  </p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Credit Costs Reference */}
        <Col md={4}>
          <Card className={theme === 'dark' ? 'bg-dark text-white border-secondary' : ''}>
            <Card.Header className={theme === 'dark' ? 'border-secondary' : ''}>
              Credit Costs
            </Card.Header>
            <Card.Body>
              {costs?.costs && (
                <div className="credit-costs">
                  {Object.entries(costs.costs).map(([action, cost]) => (
                    <div key={action} className="d-flex justify-content-between py-2 border-bottom">
                      <span>{getActionLabel(action)}</span>
                      <Badge bg="secondary">{cost} credit{cost > 1 ? 's' : ''}</Badge>
                    </div>
                  ))}
                </div>
              )}
              <small className={`d-block mt-3 ${theme === 'dark' ? 'text-light-emphasis' : 'text-muted'}`}>
                * Transcription cost is per minute of audio
              </small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Purchase Credits */}
      {!isUnlimited && (
        <Card className={theme === 'dark' ? 'bg-dark text-white border-secondary' : ''}>
          <Card.Header className={theme === 'dark' ? 'border-secondary' : ''}>
            <FiShoppingCart className="me-2" />
            Purchase Additional Credits
          </Card.Header>
          <Card.Body>
            <p className={theme === 'dark' ? 'text-light-emphasis' : 'text-muted'}>
              Need more credits? Purchase a credit pack below. Purchased credits never expire and are used after your monthly allocation runs out.
            </p>
            <Row>
              {costs?.creditPacks?.map((pack) => (
                <Col md={4} key={pack.credits}>
                  <Card className={`text-center mb-3 ${theme === 'dark' ? 'bg-secondary border-0' : 'border'}`}>
                    <Card.Body>
                      <h4 className="text-primary mb-1">{pack.credits}</h4>
                      <p className="mb-2">Credits</p>
                      <h5 className="mb-3">${pack.price}</h5>
                      <Button 
                        variant="primary" 
                        className="w-100"
                        onClick={() => handlePurchaseCredits(pack.credits)}
                        disabled={purchasing}
                      >
                        {purchasing ? 'Processing...' : 'Buy Now'}
                      </Button>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          </Card.Body>
        </Card>
      )}
    </Container>
  );
};

export default UsageDashboard;
